var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { z } from 'zod';
import { Timeouts } from './framework/Timeouts';
const zDimensionsData = z.object({
    height: z.number(),
    width: z.number(),
    x: z.number(),
    y: z.number(),
});
const zLayoutData = z.object({
    layoutId: z.string(),
    layoutName: z.string().nullable(),
    isDirty: z.boolean(),
    isLocalLayout: z.boolean(),
    clientWallInstanceId: z.string(),
    templateInformation: z.object({
        rows: z.number(),
        columns: z.number(),
        cells: z
            .array(z.object({
            row: z.number(),
            column: z.number(),
            rowSpan: z.number(),
            columnSpan: z.number(),
        }))
            .nullable(),
    }),
});
const zWallData = z.object({
    wallId: z.string(),
    wallName: z.string(),
    wallPort: z.number(),
    cliPort: z.number(),
    collaborationPort: z.number(),
    autoStart: z.boolean(),
    showInWebUi: z.boolean(),
    isPartition: z.boolean(),
    displayGroup: z
        .object({
        id: z.string(),
        name: z.string(),
        relativeDesktopLocation: zDimensionsData,
    })
        .nullable(),
    displayGroupRelativeBounds: zDimensionsData,
    desktopRelativeBounds: zDimensionsData,
    screenRectangles: z.array(zDimensionsData),
    screens: z.array(z.object({
        relativeDesktopLocation: zDimensionsData,
        relativeGroupLocation: zDimensionsData,
        displayControlState: z.number(),
        isDisplayManagementConfigured: z.boolean(),
        isDisplayConnected: z.boolean(),
        id: z.number(),
    })),
    wallStorageDirectory: z.string(),
    configLastUpdated: z.string(),
    configState: z.number(),
    validationFailures: z.array(z
        .object({
        validationCode: z.string(),
        description: z.string(),
        severity: z.number(),
    })
        .optional()),
    wallConfigType: z.number(),
    operationalState: z.number(),
    isBlueprintWall: z.boolean(),
    wallIconName: z.string(),
    wallColourName: z.string(),
    displayControlSettings: z.object({
        powerOnAtStartup: z.boolean(),
        powerOffAtShutdown: z.boolean(),
    }),
    layoutSettings: z.object({
        openLayoutOnStartup: z.number(),
        specifiedLayoutId: z.string(),
    }),
});
const datapathResponseCommandError = z.object({
    errors: z.object({
        wallId: z.array(z.string()).optional(),
        layoutId: z.array(z.string()).optional(),
    }),
    title: z.string(),
    status: z.number(),
    traceId: z.string(),
});
const datapathResponseGetCurrentLayout = zLayoutData;
const datapathResponseGetWalls = z.array(zWallData);
const datapathResponseServerInfo = z.object({
    serialNumber: z.string(),
    dongleType: z.number(),
    isMultiWallSupported: z.boolean(),
    serverTopology: z.object({
        desktopBounds: zDimensionsData,
        displayGroups: z.array(z.object({
            id: z.string(),
            name: z.string(),
            relativeDesktopLocation: zDimensionsData,
        })),
        screens: z.array(z.object({
            relativeDesktopLocation: zDimensionsData,
            relativeGroupLocation: zDimensionsData,
            displayControlState: z.number(),
            isDisplayManagementConfigured: z.boolean(),
            isDisplayConnected: z.boolean(),
            id: z.number(),
        })),
        walls: z.array(zWallData),
    }),
    serverApplicationVersion: z.string(),
    diagnosticSuitePort: z.number(),
});
const datapathResponseWallGetLayouts = z.array(zLayoutData);
function isResp(schema, resp) {
    return schema.safeParse(resp).success;
}
export class Datapath {
    constructor(http, state, log, framework) {
        this.http = http;
        this.state = state;
        this.log = log;
        // other class properties and constructor
        this.controlPort = '19821'; // default port for system information
        this.detectedChannels = {
            inputs: [],
            outputs: [],
        };
        this.timeouts = new Timeouts();
        this.busyTimeout = this.timeouts.create();
        // controls
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        this.detected = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return {
                    videoInputs: this.detectedChannels.inputs,
                    videoOutputs: this.detectedChannels.outputs,
                };
            }),
        };
        this.firmwareVersion = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.firmwareVersion.value;
            }),
        };
        this.serialNumber = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.serialNumber.value;
            }),
        };
        // Outputs are walls and inputs are layouts.
        this.videoMatrixRoutes = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const [, wallPort] = key.split('.');
                if (wallPort === undefined) {
                    this.log.error(`key: unknown key ${JSON.stringify(key)}`);
                }
                const resp = yield this.httpGetJson({ cmd: '/api/v1/Layouts/current', port: wallPort }, datapathResponseGetCurrentLayout.or(datapathResponseCommandError), tags);
                return 'layoutId' in resp
                    ? `${resp.layoutId}`
                    : this.state.videoMatrixRoutes.values[key];
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const [, wallPort] = key.split('.');
                this.state.busy.update(true);
                this.busyTimeout.set(() => this.state.busy.update(false), 2000);
                const requestOptions = {};
                requestOptions.tags = tags;
                const resp = yield this.http.request('post', `:${wallPort}/api/v1/Layouts/${value}/open`, requestOptions);
                // This command does not return JSON or any other data, so use status response to
                // indicate successful layout change.
                return resp.status === 200 ? value : this.state.videoMatrixRoutes.values[key];
            }),
        };
        // polling
        this.polls = [
            { intervalMs: 5000, controls: ['videoMatrixRoutes'] },
            {
                // Check for new video walls and layouts.
                intervalMs: 5 * 60 * 1000,
                function: () => __awaiter(this, void 0, void 0, function* () {
                    yield this.queryLayouts(true);
                }),
            },
            {
                // Prevent idle timeouts in case no matrix is defined.
                intervalMs: 10000,
                function: () => __awaiter(this, void 0, void 0, function* () {
                    yield this.ping(true);
                }),
            },
        ];
    }
    // phase callbacks
    onConfigure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
            const resp = yield this.httpGetJson({ cmd: '/api/v1/Server/information', port: this.controlPort }, datapathResponseServerInfo, ['initialize']);
            this.state.firmwareVersion.update(resp.serverApplicationVersion);
            this.state.serialNumber.update(resp.serialNumber);
            // Detect the walls and layouts to create detected channels.
            yield this.queryLayouts(false);
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    onInitialize(credentials, config) {
        return __awaiter(this, void 0, void 0, function* () {
            const requestOptions = {};
            requestOptions.tags = ['initialize'];
            try {
                this.ping(false);
                return 'connected';
            }
            catch (error) {
                return 'verifyFailed';
            }
            return 'verifyFailed';
        });
    }
    // other class methods
    detectedAreEqual(chans1, chans2) {
        return (this.detectedEqualSupport(chans1.inputs, chans2.inputs) &&
            this.detectedEqualSupport(chans1.outputs, chans2.outputs));
    }
    detectedEqualSupport(a1, a2) {
        if (a1.length === a2.length) {
            for (const e1 of a1) {
                if (!a2.find(e2 => e2.code === e1.code))
                    return false;
            }
            for (const e2 of a2) {
                if (!a1.find(e1 => e2.code === e1.code))
                    return false;
            }
        }
        else {
            return false;
        }
        return true;
    }
    httpGetJson(cmd, schema, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const requestOptions = {};
            requestOptions.tags = tags;
            const resp = yield this.http.requestJson('get', `:${cmd.port}${cmd.cmd}`, requestOptions);
            if (!isResp(schema, resp)) {
                throw new Error(`unexpected response: ${JSON.stringify(resp)}`);
            }
            return resp;
        });
    }
    ping(isPoll) {
        return __awaiter(this, void 0, void 0, function* () {
            const requestOptions = {};
            requestOptions.tags = isPoll ? ['poll'] : ['initialize'];
            yield this.http.request('get', `:${this.controlPort}/api/v1/Server/oem`, requestOptions);
        });
    }
    queryLayouts(isPoll) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const tags = isPoll ? ['get.detected', 'poll'] : ['get.detected'];
            const detectedVideoWalls = [];
            const videoWalls = yield this.httpGetJson({ port: this.controlPort, cmd: '/api/v1/Walls' }, datapathResponseGetWalls, tags);
            // Discover the active walls (2 = 'starting', 3 = 'started').
            for (const videoWall of videoWalls !== null && videoWalls !== void 0 ? videoWalls : []) {
                if (videoWall.operationalState > 1) {
                    const vwData = {
                        wallPort: videoWall.wallPort.toString(),
                        wallId: videoWall.wallId,
                        wallName: videoWall.wallName,
                        layouts: [],
                    };
                    const layouts = yield this.httpGetJson({ port: videoWall.wallPort.toString(), cmd: '/api/v1/layouts' }, datapathResponseWallGetLayouts.or(datapathResponseCommandError), tags);
                    if (!('errors' in layouts)) {
                        for (const layout of layouts !== null && layouts !== void 0 ? layouts : []) {
                            vwData.layouts.push({
                                layoutId: layout.layoutId,
                                layoutName: (_a = layout.layoutName) !== null && _a !== void 0 ? _a : '',
                            });
                        }
                    }
                    detectedVideoWalls.push(vwData);
                }
            }
            // Check if any of the discovered walls and layouts are new channels and see if there are
            // any changes.
            const tmpDetectedChannels = { inputs: [], outputs: [] };
            // Create detected channels from video walls (as outputs) and layouts (as inputs).
            for (const videoWall of detectedVideoWalls !== null && detectedVideoWalls !== void 0 ? detectedVideoWalls : []) {
                const wallId = `${videoWall.wallId}.${videoWall.wallPort}`;
                tmpDetectedChannels.outputs.push({ code: wallId, name: videoWall.wallName });
                const layouts = videoWall.layouts;
                layouts.forEach(layout => {
                    const key = layout.layoutId;
                    const name = layout.layoutName;
                    // TODO: add video wall info to support limiting layouts to their respective video
                    // wall.
                    tmpDetectedChannels.inputs.push({ code: key, external: false, name: name });
                });
            }
            if (!this.detectedAreEqual(tmpDetectedChannels, this.detectedChannels)) {
                this.detectedChannels.inputs.length = 0;
                this.detectedChannels.outputs.length = 0;
                this.detectedChannels.inputs.push(...tmpDetectedChannels.inputs);
                this.detectedChannels.outputs.push(...tmpDetectedChannels.outputs);
                this.state.detected.update({
                    videoInputs: this.detectedChannels.inputs,
                    videoOutputs: this.detectedChannels.outputs,
                });
            }
        });
    }
}
// static properties
Datapath.driverType = 'httpDriver';
Datapath.key = '-NbZqHJuBv5WDqxPdweG';
Datapath.httpRequestOptions = { scheme: 'http', idleTimeoutMs: 20000 };
